import { flex } from "styled-system";
import get from "lodash/get";

export const ROBOTO = "'Helvetica Neue', 'Roboto', sans-serif";
const CURSOR_POINTER = { cursor: "pointer" };
const SOLID_BORDER = { borderStyle: "solid" };

export const COLORS = {
  moonstoneblue: "#67B3BC",
  black: "#000",
  white: "#fff",
  gray: [
    "#B8C4D3",
    "#7c7c7c",
    "#555555",
    "#333333",
    "#D8D8D8",
    "#767778",
    "#151D29",
    "#E4E7EB",
    "#F5F7FA",
    "#9AA5B1",
    "#CBD2D9",
    "#54585E",
    "#EEF1F4"
  ],
  povodarkgray: [
    "#D6D6D6",
    "#ADADAD",
    "#858585",
    "#5C5C5C",
    "#333333",
    "#222222"
  ],
  povoyellow: [
    "#FFFDCC",
    "#FFFB99",
    "#FFF966",
    "#FFF733",
    "#FFF500",
    "#FFE500"
  ],
  navy: [
    "#CCB8E3",
    "#998AC7",
    "#665CAB",
    "#332E8F",
    "#000073",
    "#000067",
    "#00005E",
    "#000056",
    "#000049"
  ],
  blue: [
    "#CCCCFF",
    "#9999FF",
    "#6666FF",
    "#9749FF",
    "#0000FF",
    "#0000CC",
    "#0099FF",
    "#D0CCF1",
    "#0015C4"
  ],
  cyan: ["#EEFFFF", "#CCFFFF", "#99FFFF", "#59FFFF", "#00FFFF", "#00CCCC"],
  purple: [
    "#E7CCF8",
    "#CF99F1",
    "#66FFFF",
    "#9F33E3",
    "#8700DC",
    "#8700AA",
    "#E5E8F9",
    "#9749FF",
    "#D5B6FF",
    "#E8E9FD",
    "#EADBFF",
    "#C092FF"
  ],
  magenta: ["#FFCCFF", "#FF99FF", "#FF66FF", "#FF4DFF", "#FF00FF", "#CC00CC"],
  red: [
    "#FFCCCC",
    "#FF0046",
    "#CC0046",
    "#FEDEDE",
    "#EBCCCC",
    "#C9184A",
    "#DF7492",
    "#E9A3B7"
  ],
  green: [
    "#CCFFCC",
    "#00CC00",
    "#009900",
    "#DFF0D8",
    "#D0E9C6",
    "#3C763D",
    "#04AA66"
  ],
  gold: ["#FFFAD2", "#D4AF37", "#D48837"],
  silver: ["#E0E0E0", "#808080", "#606060", "#FCFCFC"],
  pink: ["#F628C2", "#9481B6", "#FEEAF9"]
};

export const BOX_SHADOWS = ["0px 2px 4px rgba(0, 0, 0, 0.24)"];

export const iconThemes = {
  black: COLORS.black,
  white: COLORS.white,
  gray: COLORS.gray[1],
  navy: COLORS.navy[4],
  blue: COLORS.blue[4],
  cyan: COLORS.cyan[4],
  purple: COLORS.purple[7],
  magenta: COLORS.magenta[4],
  red: COLORS.red[5],
  green: COLORS.green[6],
  gold: COLORS.gold[1],
  silver: COLORS.silver[1],
  povoyellow: COLORS.povoyellow[4],
  povodarkgray: COLORS.povodarkgray[4]
};

export const buttonThemes = {
  blue: {
    color: COLORS.blue[4],
    hoverColor: COLORS.blue[5],
    backgroundColor: COLORS.blue[0]
  },
  navy: {
    color: COLORS.navy[4],
    hoverColor: COLORS.navy[5],
    backgroundColor: COLORS.navy[0]
  },
  purple: {
    color: COLORS.white,
    hoverColor: COLORS.purple[7],
    backgroundColor: COLORS.purple[7]
  },
  gold: {
    color: COLORS.gold[1],
    hoverColor: COLORS.gold[2],
    backgroundColor: COLORS.gold[0]
  },
  silver: {
    color: COLORS.silver[1],
    hoverColor: COLORS.silver[2],
    backgroundColor: COLORS.silver[0]
  },
  black: {
    color: COLORS.black,
    hoverColor: COLORS.gray[1],
    backgroundColor: COLORS.gray[0]
  },
  cyan: {
    color: COLORS.cyan[4],
    hoverColor: COLORS.cyan[5],
    backgroundColor: COLORS.cyan[0]
  },
  magenta: {
    color: COLORS.magenta[4],
    hoverColor: COLORS.magenta[5],
    backgroundColor: COLORS.magenta[0]
  },
  red: {
    color: COLORS.red[5],
    hoverColor: COLORS.red[5],
    backgroundColor: COLORS.red[0]
  },
  green: {
    color: COLORS.green[1],
    hoverColor: COLORS.green[2],
    backgroundColor: COLORS.green[0]
  },
  povoyellow: {
    color: COLORS.povoyellow[4],
    hoverColor: COLORS.povoyellow[5],
    backgroundColor: COLORS.povoyellow[0]
  },
  povodarkgray: {
    color: COLORS.povodarkgray[4],
    hoverColor: COLORS.povodarkgray[5],
    backgroundColor: COLORS.povodarkgray[0]
  },
  singpass: {
    color: "#cf0b15",
    hoverColor: "#87070e",
    backgroundColor: COLORS.red[0]
  }
};

export const baseTheme = {
  breakpoints: ["40em", "52em", "64em"],
  fontSizes: [12, 14, 16, 18, 20, 24, 30],
  lineHeights: ["13px", "19px", "24px"],
  space: [0, 4, 8, 12, 16, 20, 24, 28, 32],
  radii: [0, 4, 8, 12, 16, 20],
  colors: {
    ...COLORS,
    primary: iconThemes.purple,
    secondary: iconThemes.magenta,
    tertiary: iconThemes.cyan,
    text: COLORS.gray[3],
    heading: COLORS.gray[6],
    label: COLORS.gray[6],
    badge: {
      backgroundColor: COLORS.magenta[3],
      color: COLORS.white
    },
    stepper: {
      activeColor: COLORS.magenta[3],
      inactiveColor: COLORS.gray[0]
    },
    card: COLORS.blue[4],
    input: COLORS.blue[4],
    button: buttonThemes.purple,
    cta: buttonThemes.blue,
    icon: iconThemes.blue,
    info: iconThemes.cyan,
    error: iconThemes.red,
    success: iconThemes.green,
    pending: iconThemes.gold
  }
};
const heading = {
  fontFamily: "heading",
  lineHeight: "heading",
  fontWeight: "heading"
};

const baseInputStyles = {
  backgroundColor: COLORS.white,
  outline: 0,
  fontFamily: ROBOTO,
  fontSize: baseTheme.fontSizes[2],
  p: 3,
  color: COLORS.gray[3],
  borderRadius: baseTheme.radii[0],
  borderWidth: "0 0 1px 0",
  borderColor: COLORS.gray[0],
  boxShadow: "0 0 0 #CBD2D9 inset;",
  "&:disabled": {
    opacity: 1
  },
  padding: "0 0 8px 0",
  "&:focus": {
    borderBottom: `1px solid ${COLORS.purple[7]}`
  },
  "::placeholder, ::-webkit-input-placeholder": {
    color: COLORS.gray[9]
  },
  ":-ms-input-placeholder": {
    color: COLORS.gray[9]
  },
  "&.invalid": {
    borderColor: "error",
    borderWidth: "0 0 1px 0"
  },
  "&.active": {
    borderColor: "primary"
  },
  "&.disabled": {
    borderColor: COLORS.gray[9],
    color: COLORS.gray[9]
  }
};

export const selectOptionStyles = {
  control: {
    minHeight: 42,
    fontSize: baseTheme.fontSizes[2],
    boxShadow: "0 0 0 #CBD2D9 inset;",
    borderRadius: baseTheme.radii[0],
    borderColor: COLORS.gray[10],
    borderWidth: "0 0 1px 0",
    "&:hover": {
      color: "none"
    }
  },
  option: {
    padding: "10px",
    fontSize: baseTheme.fontSizes[1]
  },
  dropdownIndicator: {
    color: "input"
  },
  valueContainer: {
    padding: "0"
  },
  noOptionsMessage: {
    fontSize: baseTheme.fontSizes[0],
    lineHeight: baseTheme.lineHeights[0]
  },
  placeholder: {
    fontSize: baseTheme.fontSizes[2],
    lineHeight: baseTheme.lineHeights[1]
  },
  menuList: {
    paddingTop: "0",
    paddingBottom: "0.375rem"
  }
};

const theme = {
  ...baseTheme,
  svg: {
    margin: 0
  },
  fonts: {
    body: ROBOTO,
    heading: ROBOTO
  },
  fontWeights: {
    body: 400,
    heading: 700,
    bold: 700
  },
  lineHeights: {
    body: 1.5,
    heading: 1.125
  },
  text: {
    heading,
    caps: {
      textTransform: "uppercase",
      letterSpacing: "0.1em"
    }
  },
  variants: {
    dark: {
      backgroundColor: COLORS.blue[2],
      borderColor: COLORS.blue[2],
      color: COLORS.white,
      a: {
        color: COLORS.cyan[4],
        textDecoration: "none",
        fontWeight: "bold",
        "&:hover": {
          color: COLORS.cyan[2]
        },
        "&:visited": {
          color: COLORS.cyan[4]
        }
      },
      borderWidth: 0,
      borderRadius: baseTheme.radii[1],
      ...SOLID_BORDER
    },
    light: {
      backgroundColor: COLORS.cyan[0],
      borderColor: COLORS.cyan[0],
      color: COLORS.gray[3],
      a: {
        color: "cta.color",
        textDecoration: "none",
        fontWeight: "bold",
        "&:hover": {
          color: "cta.hoverColor"
        },
        "&:visited": {
          color: "cta.color"
        }
      },
      borderWidth: 0,
      borderRadius: baseTheme.radii[1],
      ...SOLID_BORDER
    },
    main: {
      borderRadius: [0, 2],
      bg: "white",
      p: 6,
      boxShadow: "0 2px 8px 0 rgba(0,0,0,.1), 0 1px 2px 0 rgba(0,0,0,.06)",
      "label a": {
        color: "cta.color",
        textDecoration: "none",
        "&:hover": {
          color: "cta.hoverColor"
        },
        "&:visited": {
          color: "cta.color"
        }
      },
      ".page-title > span > span": {
        color: "cta.color"
      },
      ul: {
        marginLeft: "1rem",
        listStyle: "disc inside"
      },
      ol: {
        marginLeft: "1rem",
        listStyle: "decimal inside"
      },
      ".__react_component_tooltip.show": {
        opacity: 1
      }
    },
    transparent: {
      bg: "transparent",
      boxShadow: "none"
    },
    banner: {
      borderRadius: [0, 2],
      bg: "transparent",
      p: 0,
      img: {
        borderRadius: [0, 2]
      }
    },
    curvedBanner: {
      borderRadius: [3],
      bg: "transparent",
      p: 0,
      img: {
        borderRadius: [3]
      },
      boxShadow: "2px 2px 4px 0 rgba(0,0,0,0.3)"
    },
    roundedBanner: {
      borderRadius: [1],
      bg: "transparent",
      p: 0,
      img: {
        borderRadius: [1]
      },
      boxShadow: "2px 2px 4px 0 rgba(0,0,0,0.3)"
    },
    paymentLogo: {
      borderWidth: "1px",
      borderColor: COLORS.gray[1],
      borderRadius: "18px",
      width: "36px",
      height: "36px",
      display: flex,
      bg: COLORS.gray[4]
    },
    cardWrapper: {
      backgroundColor: COLORS.purple[6],
      borderRadius: baseTheme.radii[3]
    },
    borderSection: {
      ...SOLID_BORDER,
      padding: "1.5rem",
      boxShadow: BOX_SHADOWS[0],
      borderWidth: "4px",
      borderRadius: baseTheme.radii[2],
      borderColor: "card",
      color: "card",
      svg: {
        fill: "card"
      },
      img: {
        borderRadius: [1]
      }
    },

    borderSectionFull: {
      ...SOLID_BORDER,
      boxShadow: "2px 2px 4px 0 rgba(0,0,0,0.3)",
      borderWidth: "3px",
      borderRadius: baseTheme.radii[5],
      borderColor: "card",
      color: "card",
      svg: {
        fill: "card"
      },
      img: {
        borderRadius: [1]
      }
    },
    inlineMessage: {
      float: "right",
      marginRight: "1rem",
      marginTop: "-2rem",
      fontWeight: "500",
      "&.valid": {
        color: "success"
      },
      "&.invalid": {
        color: "error"
      },
      fontSize: ["0.625rem", "0.75rem", "1rem"]
    },
    staticContentBoxError: {
      backgroundColor: COLORS.red[3],
      borderColor: COLORS.red[4],
      color: COLORS.red[2]
    },
    staticContentBoxSuccess: {
      backgroundColor: COLORS.green[3],
      borderColor: COLORS.green[4],
      color: COLORS.green[5]
    },
    faqs: {
      borderRadius: [0, 2],
      bg: "white",
      p: 0,
      boxShadow: "0 2px 8px 0 rgba(0,0,0,.1), 0 1px 2px 0 rgba(0,0,0,.06)",
      lineHeight: "1.5",
      ".faq-item a": {
        fontWeight: "bold",
        color: "cta.color",
        textDecoration: "none",
        "&:hover": {
          color: "cta.hoverColor"
        },
        "&:visited": {
          color: "cta.color"
        }
      },
      ".page-title > span > span": {
        color: "cta.color"
      },
      "svg > path:nth-of-type(2)": {
        fill: "cta.color"
      },
      ".faq-item": {
        alignItems: "top",
        margin: "auto",
        backgroundColor: "white",
        borderRadius: 0,
        flexDirection: "column",
        padding: "0",
        justifySelf: "center",
        borderBottom: "1px solid #CBD2D9",
        width: ["100%"],
        ".see-more-icon": {
          cursor: "pointer",
          fontWeight: "bold",
          fontSize: "12px"
        },
        ul: {
          marginLeft: "1rem",
          padding: "1rem",
          listStyle: "disc"
        },
        ol: {
          marginLeft: "1rem",
          padding: "1rem",
          listStyle: "decimal"
        }
      }
    },
    stickyFooter: {
      position: "fixed",
      bottom: 0,
      left: 0,
      width: "100%",
      zIndex: 1000,
      padding: "0.75rem",
      height: "85px",
      marginTop: "-77px",
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: COLORS.white,
      boxShadow: "rgba(0, 0, 0, 0.2) 0px 4px 5px 3px",
      ".summary": {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "center",
        marginLeft: "20px",
        letterSpacing: "0.1554px",
        lineHeight: "25px",
        fontSize: "14px",
        fontWeight: 700
      },
      ".summary-title": {
        fontFamily: ROBOTO,
        whiteSpace: "nowrap",
        fontSize: "16px",
        lineHeight: "19px",
        fontWeight: 400,
        color: "#7B8794"
      },
      ".discounted-price": {
        fontFamily: ROBOTO,
        whiteSpace: "nowrap",
        fontSize: "16px",
        lineHeight: "19px",
        fontWeight: 400,
        textDecoration: "line-through",
        color: COLORS.purple[11]
      },
      ".summary-label": {
        fontFamily: ROBOTO,
        fontSize: "24px",
        lineHeight: "29px",
        color: "primary"
      },
      ".summary-message": {
        fontFamily: ROBOTO,
        whiteSpace: "nowrap",
        fontSize: "16px",
        lineHeight: "19px",
        fontWeight: 400
      },
      svg: {
        margin: "auto 0"
      }
    },
    carousel: {
      padding: "16px",
      ".indicator": {
        margin: "0 4px",
        cursor: "pointer",
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: COLORS.blue[0],
        display: "inline-block"
      }
    },
    sectionHeader: {
      div: {
        textAlign: "center"
      },
      ".heading": {
        fontSize: ["20px", "20px", "28px"],
        lineHeight: ["24px", "24px", "34px"],
        paddingTop: ["12px", "12px", "46px"],
        paddingBottom: "8px",
        fontWeight: 700,
        color: COLORS?.black
      },
      ".subHeading": {
        fontSize: "14px",
        lineHeight: "17px",
        paddingBottom: ["30px", "30px", "38px"],
        color: COLORS?.black
      }
    },
    tabNavigator: {
      textAlign: "center",
      mx: "auto",
      width: ["100%", "100%", "390px"],
      alignItems: "center",
      justifyContent: "center",
      px: "12px",
      ".activeTabBox": {
        flex: 1,
        borderBottom: `2px solid ${COLORS.gray[3]}`,
        py: "6px",
        cursor: "pointer"
      },
      ".activeTabBox .tabText": {
        fontWeight: 700,
        fontSize: "16px",
        lineHeight: "19px",
        color: COLORS.gray[3]
      },
      ".inactiveTabBox": {
        flex: 1,
        borderBottom: `2px solid ${COLORS.gray[7]}`,
        py: "6px",
        cursor: "pointer"
      },
      ".inactiveTabBox .tabText": {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "19px",
        color: COLORS.gray[1]
      }
    },
    planCardContainer: {
      backgroundColor: "#F5F7FA",
      paddingBottom: "24px",
      ".cardHeading": {
        color: COLORS.gray[2],
        lineHeight: "19px",
        textAlign: "center"
      },
      ".tagBox": {
        borderRadius: "10px 10px 0 0",
        background: COLORS.purple,
        width: "165px",
        height: "31px",
        justifyContent: "center",
        alignItems: "center",
        marginBottom: "-10px",
        flexDirection: "column"
        // p: "16px"
        // pb:0,
      }
    },
    ".tagRecommend": {
      backgroundColor: COLORS.black,
      mx: "8px",
      my: "12px",
      width: ["calc( 100%)", "calc( 33.33% - 16px )", "calc( 33.33% - 16px )"]
    },
    planouterContainer: {
      mx: "6px",
      my: "12px",
      width: ["calc( 100%)", "calc( 33.33% - 13px )", "calc( 33.33% - 13px )"],
      ".plansCard": {
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.24)",
        borderRadius: "12px 12px 12px 12px",
        backgroundColor: COLORS.white,

        my: "12px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        ".headerBox": {
          borderRadius: "10px 10px 0 0",
          background: COLORS.blue[3],
          padding: "24px 16px 16px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "100px"
          // p: "16px"
          // pb:0,
        },

        ".headerTitle": {
          fontSize: "32px",
          fontWeight: 800,
          lineHeight: "36px",
          marginBottom: "4px",

          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden"
        },
        ".planTitle": {
          fontSize: "14px",
          lineHeight: "20px",
          fontWeight: 700,
          // marginBottom: "4px",
          wordBreak: "break-word"
        },
        ".priceTitle": {
          fontSize: "24px",
          fontWeight: 700,
          whiteSpace: "nowrap"
        },
        ".planDescription": {
          fontSize: "14px",
          fontWeight: 400,
          wordBreak: "break-word",
          textAlign: "left"
        },
        ".priceDescription": {
          fontSize: "14px",
          fontWeight: 400,
          wordBreak: "break-word"
        },
        ".plansubTitle": {
          // lineHeight: "1.5rem",
          fontWeight: 700,
          wordBreak: "break-word"
        },
        ".contentBox": {
          p: "14px",
          borderRadius: "0 0px 16px 16px",
          flexDirection: "column",
          height: "100%",
          justifyContent: "flex-end"
        },
        ".planPrice": {
          fontWeight: "700",
          color: COLORS.gray[3],
          fontSize: "14px"
        },
        ".planRecurrence": {
          fontSize: "12px",
          lineHeight: "14px",
          fontWeight: 400
        },
        ".prepaidButton": {
          borderRadius: "20px",
          width: "40px",
          height: "40px",
          minWidth: "40px",
          textAlign: "center"
        },
        ".postpaidButton": {
          borderRadius: "16px",
          height: "32px",
          minWidth: "65px",
          textAlign: "center",
          p: 3
        },
        ".wrapper": {
          overflow: "hidden",
          borderRadius:
            "20px 0 20px 0" /* Apply border radius to top left and top right corners */
        }
      }
    },
    featureList: {
      ".bulletBox": {
        width: "20px",
        minWidth: "20px"
      },
      ".feature": {
        fontSize: "12px",
        lineHeight: "14px",
        my: "4px",
        color: COLORS.gray[3]
      },
      ".viewmore": {
        fontSize: "14px",
        lineHeight: "16px",
        textDecoration: "underline",
        padding: "4px",
        color: COLORS.gray[3],
        cursor: "pointer"
      }
    },
    ".seeall": {
      color: COLORS.green[6],
      fontSize: "14px",
      lineHeight: "16px",
      textDecoration: "underline",
      textAlign: "center",
      cursor: "pointer"
    },
    noConfigBox: {
      backgroundColor: "#F5F7FA",
      px: "12px",
      py: "60px",
      textAlign: "center",
      mx: "auto",
      ".heading": {
        fontSize: "20px",
        lineHeight: "24px",
        fontWeight: 700,
        color: COLORS.gray[9]
      },
      ".subheading": {
        fontSize: "14px",
        lineHeight: "14px",
        color: COLORS.gray[9]
      }
    },
    orderSummaryCard: {
      border: `4px solid ${baseTheme.colors.primary}`,
      borderRadius: "24px",
      ".subText": {
        fontSize: "16px",
        lineHeight: "19px",
        fontWeight: "700",
        overflowWrap: "break-word"
      },
      ".priceText": {
        fontSize: "16px",
        lineHeight: "19px",
        fontWeight: "500"
        // overflowWrap: "break-word"
      },
      ".subTotalBox": {
        px: "16px",
        py: "2px",
        backgroundColor: COLORS.gray[7],
        justifyContent: "space-between"
      },
      ".sectionHeading": {
        justifyContent: "space-between"
      },
      ".totalBox": {
        padding: "8px 19px 12px 16px"
      },
      ".totalText": {
        color: COLORS.white,
        fontSize: "24px",
        lineHeight: "28px",
        fontWeight: "700"
      },
      ".totalDesc": {
        color: COLORS.white,
        fontSize: "14px",
        lineHeight: "17px"
      }
    },
    wrapper: {
      overflow: "hidden",
      borderRadius:
        "20px 20px 0 0" /* Apply border radius to top left and top right corners */
    },
    recommendedTag: {
      width: "30px",
      height: "41px",
      backgroundColor: "#ff7700",
      display: flex,
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
      borderRadius:
        "0 0 20px 0" /* Apply border radius to the bottom right corner */
    }
  },
  buttons: {
    primary: {
      ...CURSOR_POINTER,
      fontFamily: ROBOTO,
      fontWeight: 700,
      padding: "0.75rem 1rem",
      color: COLORS.white,
      backgroundColor: "button.color",
      borderColor: "button.color",
      borderWidth: 2,
      borderRadius: 48,
      ...SOLID_BORDER,
      "&:hover": {
        backgroundColor: "button.hoverColor",
        borderColor: "button.hoverColor"
      },
      ":disabled": {
        pointerEvents: "none",
        cursor: "not-allowed",
        opacity: 0.4
      }
    },
    secondary: {
      variant: "buttons.primary",
      backgroundColor: COLORS.white,
      borderColor: COLORS.white,
      color: "button.color",
      "&:hover": {
        backgroundColor: "button.backgroundColor",
        borderColor: "button.hoverColor"
      }
    },
    outline: {
      variant: "buttons.primary",
      backgroundColor: COLORS.white,
      borderColor: "button.color",
      color: "button.color",
      "&:hover": {
        backgroundColor: "button.backgroundColor"
      },
      "&:focus": {
        outline: "none"
      }
    },
    link: {
      variant: "buttons.primary",
      backgroundColor: "transparent",
      borderColor: "transparent",
      color: "button.color",
      textDecoration: "underline",
      fontWeight: "normal",
      "&:hover": {
        backgroundColor: "transparent",
        borderColor: "transparent"
      }
    },
    closeButton: {
      float: "right",
      margin: "0 !important",
      fontWeight: "500",
      fontSize: "1.5rem",
      color: "error",
      backgroundColor: "transparent",
      padding: "0 !important",
      width: "auto",
      minWidth: "0 !important",
      borderWidth: "0"
    },
    icon: {
      padding: "0 !important",
      width: "auto",
      minWidth: "0 !important",
      borderWidth: "0",
      margin: "auto 0 !important",
      backgroundColor: "transparent"
    },
    headerButton: {
      variant: "buttons.outline",
      borderColor: COLORS.white,
      color: "button.color",
      width: "fit-content",
      borderRadius: "0 !important",
      padding: "0.5rem !important",
      minWidth: "0 !important",
      "&:hover": {
        borderColor: COLORS.white,
        color: "button.hoverColor"
      }
    },
    sideMenuLink: {
      backgroundColor: COLORS.gray[3],
      color: COLORS.white,
      fontWeight: "500",
      fontSize: "1.2rem",
      borderRadius: "0 !important",
      margin: "0 !important",
      "&:hover": {
        color: "button.hoverColor"
      }
    }
  },
  textSizes: {
    xs: {
      fontSize: baseTheme.fontSizes[0]
    },
    sm: {
      fontSize: baseTheme.fontSizes[1]
    },
    lg: {
      fontSize: baseTheme.fontSizes[2]
    },
    xl: {
      fontSize: baseTheme.fontSizes[3]
    }
  },
  buttonSizes: {
    small: {
      fontSize: baseTheme.fontSizes[2],
      padding: "4px 18px"
    },
    large: {
      fontSize: baseTheme.fontSizes[4],
      padding: "12px 48px"
    }
  },
  forms: {
    input: baseInputStyles,
    select: baseInputStyles,
    textarea: {
      resize: "vertical"
    },
    label: {
      fontSize: "1rem",
      mb: 1
    },
    radio: {},
    checkbox: {
      svg: {
        fill: "input"
      }
    }
  },
  ".tagText": {
    fontSize: "1rem",
    lineHeight: "24px",
    fontWeight: "700",
    fontFamily: "SF Pro Display",
    fontStyle: "normal"
  }
};

export const TAGTHEME = {
  theme1: "#0015C4",
  theme2: "#8700DC",
  theme3: "#028090",
  theme4: "#F628C2",
  theme5: "#333333"
};

export const getButtonColorVariant = (buttonTheme, variant = "primary") => {
  let style = get(theme, `variants.buttons.${variant}`, {});
  const color = get(buttonTheme, "color", COLORS.white);
  const darkColor = get(
    buttonTheme,
    "hoverColor",
    buttonThemes?.blue?.hoverColor
  );
  const lightColor = get(
    buttonTheme,
    "backgroundColor",
    buttonThemes?.blue?.backgroundColor
  );
  const boxShadow = get(buttonTheme, "boxShadow");
  const fontWeight = get(buttonTheme, "fontWeight", 700);
  const activeBackgroundColor = get(
    buttonTheme,
    "activeBackgroundColor",
    COLORS.purple[8]
  );
  const disabledColor = get(buttonTheme, "disabledColor", COLORS.gray[9]);
  const disabledBackgroundColor = get(
    buttonTheme,
    "disabledBackgroundColor",
    COLORS.gray[10]
  );
  const disabledOpacity = get(buttonTheme, "disabledOpacity", 0.4);
  switch (variant) {
    case "primary":
      style = {
        ...style,
        color: COLORS.white,
        backgroundColor: darkColor,
        borderColor: darkColor,
        fontWeight: fontWeight,
        "&:hover": {
          backgroundColor: darkColor,
          borderColor: darkColor,
          ...(boxShadow && { boxShadow: boxShadow })
        },
        "&:focus": {
          outline: "none"
        },
        "&:active": {
          backgroundColor: activeBackgroundColor,
          borderColor: activeBackgroundColor
        },
        "&:disabled": {
          color: disabledColor,
          backgroundColor: disabledBackgroundColor,
          borderColor: disabledBackgroundColor,
          opacity: disabledOpacity
        }
      };
      break;
    case "secondary":
      style = {
        ...style,
        color: lightColor,
        backgroundColor: COLORS.white,
        borderColor: color,
        "&:hover": {
          backgroundColor: COLORS.white,
          borderColor: lightColor,
          ...(boxShadow && { boxShadow: boxShadow })
        },
        "&:focus": {
          outline: "none"
        },
        "&:active": {
          backgroundColor: activeBackgroundColor
        },
        "&:disabled": {
          backgroundColor: disabledBackgroundColor,
          borderColor: disabledColor,
          color: disabledColor,
          opacity: disabledOpacity
        }
      };
      break;
    case "outline":
      style = {
        ...style,
        backgroundColor: "transparent",
        borderColor: color,
        color: color,
        "&:focus": {
          outline: "none"
        },
        "&:hover": {
          backgroundColor: "transparent",
          borderColor: color,
          ...(boxShadow && { boxShadow: boxShadow })
        }
      };
      break;
    case "link":
      style = {
        ...style,
        variant: "buttons.primary",
        backgroundColor: "transparent",
        borderColor: "transparent",
        color: color,
        textDecoration: "underline",
        fontWeight: "normal",
        "&:hover": {
          backgroundColor: "transparent",
          borderColor: "transparent"
        }
      };
      break;
    case "headerButton":
      style = {
        ...style,
        color: color,
        "&:hover": {
          color: darkColor
        }
      };
      break;
    case "textButton":
      style = {
        ...style,
        color: color,
        "&:hover": {
          color: darkColor
        }
      };
      break;
    default:
      break;
  }
  return style;
};

export const getInputLabelStateColor = (
  {
    error = false,
    invalid = false,
    touched = false,
    active = false,
    visited = false
  },
  disabled = false,
  required = false,
  themeColors = {}
) => {
  const textColor = get(themeColors, "text", COLORS.gray[10]);
  const primaryColor = get(themeColors, "primary", COLORS.purple[7]);
  const errorColor = get(themeColors, "error", COLORS.red[5]);
  const disabledColor = get(
    themeColors,
    "button.disabledColor",
    COLORS.gray[3] //color changed to  default color as per B2BCL-25485
  );

  let labelColor = textColor;
  if (error && invalid && (touched || visited) && required)
    labelColor = errorColor;
  if (active) labelColor = primaryColor;
  if (disabled) labelColor = disabledColor;

  return labelColor;
};

export const getInputStateClassName = (
  { error = false, valid, invalid = false, touched = false, active = false },
  disabled = false,
  required
) => {
  let className = "";
  if (error && invalid && touched && required) className = "invalid";
  if (valid && touched && required) className = "valid";
  if (active) className = "active";
  if (disabled) className = "disabled";

  return className;
};

export default theme;
